import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Masks } from "react-native-mask-input";
import { XStack } from "tamagui";
import { z } from "zod";

import { VerificationStatus } from "@/api/generated/models";
import { useUpdateUserProfile } from "@/api/mutations/userProfile";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { Grid, Input, MaskedInput, Select, getGridLayout } from "@/components";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";
import { dateOfBirthZodString } from "@/forms/ProfileForm";
import {
  GENDER_IDENTITY_OPTIONS,
  PRONOUN_OPTIONS,
  SEX_ASSIGNED_AT_BIRTH_OPTIONS,
} from "@/settings/constants";

const confirmDetailsSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  dateOfBirth: dateOfBirthZodString,
  sexAssignedAtBirth: z.string().min(1, "Sex assigned at birth is required"),
  genderIdentity: z.string().optional(),
  pronouns: z.string().optional(),
});

type ConfirmDetailsForm = z.infer<typeof confirmDetailsSchema>;

export default function ConfirmDetailsStep({
  onSubmitted,
}: {
  onSubmitted: () => void;
}) {
  const { data: profile } = useGetUserProfile();
  const { mutate: updateUserProfile, isPending } = useUpdateUserProfile();
  const isIdVerified =
    profile?.idVerification?.status === VerificationStatus.approved;

  const { control, handleSubmit } = useForm<ConfirmDetailsForm>({
    resolver: zodResolver(confirmDetailsSchema),
    mode: "onBlur",
    values: {
      firstName: profile?.firstName ?? "",
      lastName: profile?.lastName ?? "",
      dateOfBirth: profile?.dateOfBirth ?? "",
      sexAssignedAtBirth: profile?.sexAssignedAtBirth ?? "",
      genderIdentity: profile?.genderIdentity ?? "",
      pronouns: profile?.pronouns ?? "",
    },
  });

  const onSubmit = handleSubmit((formData) => {
    updateUserProfile({ data: formData }, { onSuccess: onSubmitted });
  });

  return (
    <>
      <Grid paddingHorizontal="$150">
        <Controller
          control={control}
          name="firstName"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input.Controlled
              label="First name"
              readonly={isIdVerified}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="lastName"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input.Controlled
              label="Last name"
              readonly={isIdVerified}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="dateOfBirth"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <MaskedInput.Controlled
              label="Date of birth"
              readonly={isIdVerified}
              mask={Masks.DATE_MMDDYYYY}
              placeholder="mm/dd/yyyy"
              inputMode="numeric"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="sexAssignedAtBirth"
          render={({ field: { ref, name, onChange, value }, fieldState }) => (
            <Select
              ref={ref}
              name={name}
              label="Sex at birth"
              disabled={isIdVerified}
              placeholder="Select"
              items={SEX_ASSIGNED_AT_BIRTH_OPTIONS}
              value={value}
              onValueChange={onChange}
              errorText={fieldState.error?.message}
              {...getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="genderIdentity"
          render={({ field: { ref, name, onChange, value } }) => (
            <Select
              ref={ref}
              name={name}
              label="Gender"
              placeholder="Select"
              items={GENDER_IDENTITY_OPTIONS}
              value={value}
              onValueChange={onChange}
              {...getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="pronouns"
          render={({ field: { ref, name, onChange, value } }) => (
            <Select
              ref={ref}
              name={name}
              label="Pronouns"
              placeholder="Select"
              items={PRONOUN_OPTIONS}
              value={value}
              onValueChange={onChange}
              {...getGridLayout(6)}
            />
          )}
        />
      </Grid>
      <XStack
        justifyContent="flex-end"
        paddingHorizontal="$150"
        marginTop="$250"
        paddingBottom="$150"
      >
        <SkipOrSubmit
          showSkip={false}
          showSubmit
          submitDisabled={isPending}
          isLast
          onSkip={onSubmit}
          onSubmit={onSubmit}
        />
      </XStack>
    </>
  );
}
